import { FastField, Field, FieldAttributes, FieldProps } from 'formik';
import React from 'react';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components/macro';
import { spacing04 } from '../../styling/spacing';
import { FieldLabel } from './FieldLabel';

export type ValidityProps = {
  $isValid?: boolean;
  $isInvalid?: boolean;
  $hasWarning?: boolean;
};

type FormFieldProps = {
  name: string;
  label: string | React.ReactNode | null;
  labelIcon?: React.ReactNode;
  type?: string;
  warning?: string;
  children: (props: FieldProps & ValidityProps) => React.ReactNode;
  className?: string;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any, react/display-name
const formFieldComponentBuilder = (FieldComponent: React.ComponentType<FieldAttributes<any>>) => (
  props: FormFieldProps,
) => (
  <FieldContainer className={props.className} data-tip={props.warning}>
    <FieldComponent name={props.name} type={props.type}>
      {({ form, field, meta }: FieldProps) => {
        const touched = meta.touched ?? false;
        const error = meta.error ?? null;
        const isValid: boolean = touched && !error;
        const isInvalid: boolean = touched && !!error;
        const showError: boolean = touched && !!error;
        const showTooltip = !!props.warning;

        return (
          <>
            {props.label != null && (
              <FieldLabel
                label={props.label}
                labelIcon={props.labelIcon}
                showError={showError}
                error={error}
                fieldName={props.name}
              />
            )}
            <InputAndErrorContainer>
              <InputContainer>
                {props.children({ form, field, meta, $isValid: isValid, $isInvalid: isInvalid })}
              </InputContainer>
            </InputAndErrorContainer>
            {showTooltip && <ReactTooltip effect="solid" delayShow={500} />}
          </>
        );
      }}
    </FieldComponent>
  </FieldContainer>
);

export const FormField = formFieldComponentBuilder(Field);

// FastFormField uses Formik's FastField to boost performance. FastField uses shouldComponentUpdate() and
// is only guaranteed to re-render in certain circumstances see https://formik.org/docs/api/fastfield.
export const FastFormField = formFieldComponentBuilder(FastField);

export const FieldContainer = styled.div`
  margin-bottom: ${spacing04};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const InputContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  min-height: 40px;
`;

export const InputAndErrorContainer = styled.div``;
